// ./core/gameengine/services/AutoPlayService.ts
import type { BetSide } from '@platform/contracts';
import type { BaseGameController } from '@core/gameengine/controller/BaseGameController';
import { getRegulatoryPolicy } from '@core/regulatory';

/**
 * AutoPlayService handles automatic betting for the game.
 * It monitors the game state and places bets automatically based on the configured settings.
 * Supports simultaneous autoplay for both sides (A and B).
 */
export class AutoPlayService {
  private controller: BaseGameController;
  private lastPhase: string = '';

  constructor(controller: BaseGameController) {
    this.controller = controller;
  }

  private isAutoplayAllowed(): boolean {
    return getRegulatoryPolicy().autoplay !== false;
  }

  /**
   * Check if autoplay is active for any side
   */
  isRunning(): boolean {
    if (!this.isAutoplayAllowed()) return false;
    const gameState = this.controller.getGameState();
    return (
      gameState.autoPlaySettings.A !== null ||
      gameState.autoPlaySettings.B !== null
    );
  }

  /**
   * Check if autoplay is active for a specific side
   */
  isRunningSide(side: BetSide): boolean {
    if (!this.isAutoplayAllowed()) return false;
    const gameState = this.controller.getGameState();
    return gameState.autoPlaySettings[side] !== null;
  }

  /**
   * Stops autoplay for this side. Auto Cash Out is left as the user set it.
   */
  private stopAutoplayAndResetState(side: BetSide): void {
    const gameState = this.controller.getGameState();
    console.log(`[AutoPlayService] Stopping autoplay for side ${side}`);
    gameState.stopAutoPlay(side);
  }

  /**
   * Place initial bets when autoplay is started
   */
  placeInitialBets(side: BetSide): void {
    if (!this.isAutoplayAllowed()) return;
    const currentPhase = this.controller.getGameState().phase;
    console.log(
      `[AutoPlayService] placeInitialBets for side ${side}, phase: ${currentPhase}`
    );

    // If we start in WAITING, process the first bet immediately.
    if (currentPhase === 'WAITING') {
      console.log(
        `[AutoPlayService] Placing initial bet for side ${side} in WAITING phase`
      );
      this.processAutoplayBet(side);
    }
    // If in ANY other phase, just arm the bet.
    // onNewRoundStart will handle processing the bet when WAITING starts.
    else {
      console.log(
        `[AutoPlayService] Arming bet for side ${side} in ${currentPhase} phase`
      );
      this.armBetForSide(side);
    }
  }

  /**
   * Update method to be called on each game loop iteration
   * This monitors phase changes and triggers bets when appropriate
   */
  update(): void {
    if (!this.isRunning()) {
      return;
    }

    const gameState = this.controller.getGameState();
    const currentPhase = gameState.phase;

    // Check stop conditions on REVEAL
    const isTransitioningToReveal =
      this.lastPhase !== 'REVEAL' && currentPhase === 'REVEAL';

    if (isTransitioningToReveal) {
      console.log(
        `[AutoPlayService] Phase transition: ${this.lastPhase} -> REVEAL, checking stop conditions`
      );
      const sides: BetSide[] = ['A', 'B'];
      for (const side of sides) {
        if (this.isRunningSide(side)) {
          this.checkStopConditionsOnReveal(side);
        }
      }
    }

    // Check for new round start to place bets
    const isTransitioningToNewRound =
      this.lastPhase !== 'WAITING' && currentPhase === 'WAITING';

    if (isTransitioningToNewRound) {
      console.log(
        `[AutoPlayService] Phase transition: ${this.lastPhase} -> WAITING, processing new round`
      );
      const sides: BetSide[] = ['A', 'B'];
      for (const side of sides) {
        if (this.isRunningSide(side)) {
          this.onNewRoundStart(side);
        }
      }
    }

    this.lastPhase = currentPhase;
  }

  /**
   * Checks stop conditions based on balance changes.
   * This is called on REVEAL and on CASHOUT.
   * Returns true if autoplay was stopped.
   */
  private checkBalanceStopConditions(side: BetSide): boolean {
    const gameState = this.controller.getGameState();
    const settings = gameState.autoPlaySettings[side];
    const progress = gameState.autoPlayProgress[side];

    if (!settings || !progress) return false;

    // const currentBalance = gameState.balance;
    const netChange = progress.netProfitLoss;

    if (settings.stopOnCashDecrease !== null) {
      const decrease = -netChange; // Negative netChange means decrease/loss
      if (decrease >= settings.stopOnCashDecrease && decrease > 0) {
        console.log(
          `[AutoPlayService] Side ${side}: Balance decreased by ${decrease}, stopping`
        );
        this.stopAutoplayAndResetState(side);
        return true;
      }
    }

    if (settings.stopOnCashIncrease !== null) {
      if (netChange >= settings.stopOnCashIncrease && netChange > 0) {
        console.log(
          `[AutoPlayService] Side ${side}: Balance increased by ${netChange.toFixed(2)}, stopping`
        );
        this.stopAutoplayAndResetState(side);
        return true;
      }
    }

    return false;
  }

  /**
   * Called when the game enters the REVEAL phase.
   * Checks for balance-based stop conditions.
   */
  private checkStopConditionsOnReveal(side: BetSide): void {
    console.log(
      `[AutoPlayService] checkStopConditionsOnReveal for side ${side}`
    );
    this.checkBalanceStopConditions(side);
  }

  /**
   * Called when a new round starts (enters WAITING phase)
   * This just triggers the main bet processing logic.
   */
  private onNewRoundStart(side: BetSide): void {
    console.log(`[AutoPlayService] onNewRoundStart called for side ${side}`);
    this.processAutoplayBet(side);
  }

  /**
   * This is the core logic loop for placing an autoplay bet.
   * 1. Check if round limit is reached. If so, stop.
   * 2. Check if BALANCE is sufficient. If not, stop.
   * 3. Place the bet (if armed or idle).
   */
  private processAutoplayBet(side: BetSide): void {
    const gameState = this.controller.getGameState();
    const settings = gameState.autoPlaySettings[side];
    const progress = gameState.autoPlayProgress[side];
    const bet = gameState.bets[side];

    console.log(
      `[AutoPlayService] processAutoplayBet for side ${side}, roundsPlayed: ${progress?.roundsPlayed}`
    );

    if (!settings || !progress) {
      console.log(
        `[AutoPlayService] No settings or progress for side ${side}, stopping.`
      );
      this.stopAutoplayAndResetState(side);
      return;
    }

    console.log(
      `[AutoPlayService] rounds played Testing ${progress.roundsPlayed}:`,
      bet
    );

    // 1. Check round limit FIRST.
    console.log(
      `[AutoPlayService] Side ${side}: Checking rounds played ${progress.roundsPlayed} against limit ${settings.rounds}`
    );
    if (
      settings.rounds !== null &&
      progress.roundsPlayed >= settings.rounds - 1
    ) {
      console.log(
        `[AutoPlayService] Side ${side}: Reached target number of rounds (${progress.roundsPlayed}/${settings.rounds}), stopping`
      );
      this.stopAutoplayAndResetState(side);
      return;
    }

    // 2. Check if Balance covers the Stake
    if (gameState.balance < bet.stake) {
      console.warn(
        `[AutoPlayService] Side ${side}: Insufficient balance (${gameState.balance}) for stake (${bet.stake}). Stopping.`
      );
      gameState.addNotification({
        type: 'error',
        message: 'Insufficient balance to continue Auto Play',
      });
      this.stopAutoplayAndResetState(side);
      return;
    }

    // 3. Place the bet if it's armed or idle
    if (bet.status === 'armed' || bet.status === 'idle') {
      console.log(
        `[AutoPlayService] Placing bet for new round on side ${side} (status: ${bet.status})`
      );
      this.placeBetForSide(side);
    } else {
      console.log(
        `[AutoPlayService] Bet for side ${side} is not idle or armed (status: ${bet.status}), not placing.`
      );
    }
  }

  /**
   * Check stop conditions after a successful cashout.
   * This includes single win AND balance changes.
   */
  checkStopConditionsOnCashout(side: BetSide, winAmount: number): void {
    const gameState = this.controller.getGameState();
    const settings = gameState.autoPlaySettings[side];

    if (!settings) {
      return;
    }

    console.log(
      `[AutoPlayService] checkStopConditionsOnCashout for side ${side}, win: ${winAmount}`
    );

    // 1. Update netProfitLoss for this side
    const progress = gameState.autoPlayProgress[side];
    if (progress) {
      const newNetProfitLoss = progress.netProfitLoss + winAmount;
      gameState.updateAutoPlayProgress(side, {
        netProfitLoss: newNetProfitLoss,
      });
      console.log(
        `[AutoPlayService] Side ${side}: Net Profit/Loss updated to ${newNetProfitLoss.toFixed(
          2
        )} after win of ${winAmount.toFixed(2)}`
      );
    }

    // 2. Check single win threshold
    if (settings.stopOnSingleWin !== null) {
      if (winAmount >= settings.stopOnSingleWin) {
        console.log(
          `[AutoPlayService] Side ${side}: Single win of ${winAmount} exceeded threshold, stopping`
        );
        // Stop immediately
        this.stopAutoplayAndResetState(side);
        return;
      }
    }

    // 3. Check balance increase/decrease conditions
    // This will use the new balance already updated in the state
    this.checkBalanceStopConditions(side);
  }

  /**
   * Place bet for a specific side
   */
  private placeBetForSide(side: BetSide): void {
    const gameState = this.controller.getGameState();
    console.log(`[AutoPlayService] placeBetForSide called for side ${side}`);
    const bet = gameState.bets[side];
    console.log(`[AutoPlayService] Side ${side} bet state:`, bet);

    // Place bet if this side is in auto mode and is idle or armed
    if (
      bet.betMode === 'auto' &&
      (bet.status === 'idle' || bet.status === 'armed')
    ) {
      console.log(
        `[AutoPlayService] Placing automatic bet for side ${side} (status: ${bet.status})`
      );
      // Use a small delay to avoid race conditions
      setTimeout(() => {
        this.controller.resolveBetAction(side);
      }, 100);
    } else {
      console.log(
        `[AutoPlayService] NOT placing bet for side ${side} - betMode: ${bet.betMode}, status: ${bet.status}`
      );
    }
  }

  /**
   * Arm bet for a specific side (for next round when in FLYING phase)
   * MODIFIED: Checks balance before arming. If insufficient, stops autoplay immediately.
   */
  private armBetForSide(side: BetSide): void {
    const gameState = this.controller.getGameState();
    const bet = gameState.bets[side];

    // 1. Check if Balance covers the Stake BEFORE arming
    if (gameState.balance < bet.stake) {
      console.warn(
        `[AutoPlayService] Side ${side}: Insufficient balance (${gameState.balance}) to arm bet. Stopping.`
      );
      gameState.addNotification({
        type: 'error',
        message: 'Insufficient balance to continue Auto Play',
      });
      this.stopAutoplayAndResetState(side);
      return; // Stop here, do not proceed to arm
    }

    // Only arm bet if this side is in auto mode and is idle
    // Don't arm if already armed, placing, or placed (to avoid conflicts)
    if (bet.betMode === 'auto' && bet.status === 'idle') {
      console.log(
        `[AutoPlayService] 🎯 Arming bet for side ${side} for next round (current status: ${bet.status})`
      );
      // Set bet status to 'armed' so the button shows correct state
      gameState.upBet(side, {
        status: 'armed',
        cashedAtX: undefined,
        win: undefined,
        error: '',
      });
    } else {
      console.log(
        `[AutoPlayService] NOT arming bet for side ${side} - betMode: ${bet.betMode}, status: ${bet.status} (bet already handled)`
      );
    }
  }

  /**
   * Start autoplay with settings
   */
  start(settings: any): void {
    if (!this.isAutoplayAllowed()) {
      console.warn(
        '[AutoPlayService] Autoplay disabled by regulatory policy — ignoring start'
      );
      return;
    }
    console.log('[AutoPlayService] Starting autoplay with settings:', settings);
    // Place initial bets for any side that has autoplay enabled
    const sides: BetSide[] = ['A', 'B'];
    for (const side of sides) {
      if (this.isRunningSide(side)) {
        console.log(`[AutoPlayService] Placing initial bet for side ${side}`);
        this.placeInitialBets(side);
      }
    }
  }

  /**
   * Stop autoplay
   */
  stop(): void {
    console.log('[AutoPlayService] Stopping autoplay');
    // The actual stop logic is handled by the game state
    // This method exists for compatibility with the controller
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this.lastPhase = '';
  }
}
